import { useState, type ReactNode } from "react";
import type { HailoCompileJob, OnnxOptions, RecommendedThresholds } from "../api/ml";
import { compileActive, compileHeadline } from "../lib/hailoCompile";
import { DEFAULT_ONNX, OPSET_CHOICES, DEFAULT_OPSET, applyTarget, effectiveOnnx, thresholdsLine } from "../lib/training";
import { PRO_TIP } from "../lib/edition";
import { Dialog } from "../ui/Dialog";

type Target = OnnxOptions["target"];

/** The three places a model can run, in the words of the person deploying it. */
const TARGETS: { value: Target; title: string; text: string; gets: string; feature?: string }[] = [
  { value: "onnxruntime", title: "ctrlX or a PC (CPU)", text: "The ctrlX vision app, or any computer running ONNX Runtime.", gets: "One .onnx file to upload." },
  { value: "tensorrt", title: "NVIDIA GPU", text: "An industrial PC with an NVIDIA graphics card.", gets: "A .zip with the model and a script that checks it runs on the GPU.", feature: "nvidia_export" },
  { value: "hailo", title: "Hailo accelerator", text: "A ctrlX CORE with a Hailo-8 chip.", gets: "A .zip that is compiled to a .hef before it runs.", feature: "hailo" },
];

/** What the person does with the file, per target. */
const NEXT: Record<Target, ReactNode[]> = {
  onnxruntime: ["Upload the .onnx file to the ctrlX vision app. Class names and pre-processing are stored inside it."],
  tensorrt: [
    "Unzip it on the GPU machine.",
    <>Run <code>python run_nvidia.py --image &lt;a frame&gt;</code> to check the model runs on the GPU.</>,
    "Optional: build a faster TensorRT engine with the command in README.txt.",
  ],
  hailo: [
    "Compile the bundle to a .hef on a machine with the Hailo Dataflow Compiler (the desktop app: Tools ▸ Compile Hailo Bundle). README.txt has the commands.",
    "Upload the .hef to the ctrlX detector app, which runs it on the Hailo chip.",
    "Until it is compiled, the model runs on the CPU at the speed you have now.",
  ],
};

/** The same target when this server has a compiler: it does the compile step for you. */
const NEXT_COMPILED: ReactNode[] = [
  "Press Compile to .hef. The server exports the bundle and compiles it on its Hailo compiler. That takes up to an hour, and you can close the window meanwhile.",
  "Download the zip: the .hef with the files the ctrlX detector app needs beside it. Upload them together.",
  "Export only gives you the bundle, for compiling on a machine of your own.",
];

const nmsTip = (t: Target) =>
  t === "hailo" ? "Not available for the Hailo target: the Dataflow Compiler cannot map NonMaxSuppression."
  : t === "tensorrt" ? "Left out for the NVIDIA target: its output dimension depends on the data, so the engine allocates for the worst case. run_nvidia.py applies NMS on the host."
  : "On: the model outputs finished boxes — nothing to post-process.\nOff: the model outputs raw boxes and per-class scores, and the app applies its own filtering and NMS (more portable).";

/**
 * Export for deployment (onnx_export_dialog.ONNXExportOptionsDialog): one question, where the model will run.
 * Everything else is a consequence of that answer or an expert override, so it sits under Advanced.
 */
export function OnnxExportDialog({ onCancel, onExport, onCompile, compileJob, modelLabel = "the latest trained model", has = () => true, defaultTarget, recommended }: {
  onCancel: () => void; onExport: (o: OnnxOptions) => void; modelLabel?: string;
  /** The thresholds this version's validation measured (ModelVersionInfo.deploy), shown under the thresholds choice; null when it recorded none. */
  recommended?: RecommendedThresholds | null;
  /** Present when this server has a Hailo compiler: the Hailo target then offers Compile to .hef beside Export. */
  onCompile?: (o: OnnxOptions) => void;
  /** The project's latest compile, so one still running (or just finished) can be reopened. */
  compileJob?: HailoCompileJob | null;
  /** Whether a licensed feature is open: the NVIDIA and Hailo targets are Pro. */
  has?: (feature: string) => boolean;
  /** Where the project's setup says the model runs; the dialog starts there when the edition allows it. */
  defaultTarget?: Target | null;
}) {
  const startOpen = !!defaultTarget && TARGETS.some((t) => t.value === defaultTarget && (!t.feature || has(t.feature)));
  const [o, setO] = useState<OnnxOptions>(() => (startOpen ? applyTarget(DEFAULT_ONNX, defaultTarget!) : DEFAULT_ONNX));
  const set = (p: Partial<OnnxOptions>) => setO((cur) => ({ ...cur, ...p }));
  const fixed = o.target !== "onnxruntime";
  const compiles = !!onCompile && o.target === "hailo";
  const row = (label: string, el: ReactNode) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}><span style={{ width: 170, flex: "none" }}>{label}</span>{el}</div>
  );
  const num = (k: "conf" | "iou" | "max_det", min: number, max: number, step: number, enabled: boolean, test: string) => (
    <input className="input" type="number" min={min} max={max} step={step} value={o[k]} disabled={!enabled} data-testid={test} style={{ width: 110 }}
      onChange={(e) => set({ [k]: Math.min(max, Math.max(min, Number(e.target.value))) } as Partial<OnnxOptions>)} />
  );
  return (
    <Dialog title="Export for deployment" onClose={onCancel} width={560} testId="onnx">
      <p className="muted" style={{ marginTop: 0 }}>Packages {modelLabel} for the device it will run on.</p>

      <div className="field-caption" style={{ marginBottom: 8 }}>Where will this model run?</div>
      <div className="target-cards" role="radiogroup" aria-label="Where will this model run?" data-testid="onnx-target">
        {TARGETS.map((t) => {
          const open = !t.feature || has(t.feature);
          return (
            <label key={t.value} className="target-card" aria-disabled={!open} title={open ? undefined : PRO_TIP}>
              <input type="radio" name="onnx-target" value={t.value} checked={o.target === t.value} disabled={!open}
                onChange={() => setO(applyTarget(o, t.value))} data-testid={`onnx-target-${t.value}`} />
              <span className="target-body">
                <span className="target-title">{t.title}
                  {t.value === "onnxruntime" && <span className="pill none">Recommended</span>}
                  {!open && <span className="pill pro">Pro</span>}</span>
                <span className="muted">{t.text}</span>
                <span className="target-gets">{t.gets}</span>
              </span>
            </label>
          );
        })}
      </div>

      <div className="next-steps" data-testid="onnx-next">
        <div className="field-caption">What happens next</div>
        <ol className="steps">{(compiles ? NEXT_COMPILED : NEXT[o.target]).map((step, i) => <li key={i}>{step}</li>)}</ol>
        {compiles && compileJob && (
          <p className="muted" data-testid="onnx-compile-last">
            Last compile{compileJob.model_version ? ` (model v${compileJob.model_version})` : ""}: {compileHeadline(compileJob)}
            {compileActive(compileJob) || compileJob.status === "completed" ? " Press Compile to .hef to open it." : ""}
          </p>
        )}
      </div>

      <details className="advanced" data-testid="onnx-advanced">
        <summary>Advanced</summary>
        <div className="advanced-body">
          {row("ONNX opset:", (
            <select className="select" value={o.opset} data-testid="onnx-opset" style={{ width: 170 }} onChange={(e) => set({ opset: Number(e.target.value) })}>
              {OPSET_CHOICES.map((v) => <option key={v} value={v}>{v === DEFAULT_OPSET ? `${v}  —  recommended` : v}</option>)}
            </select>
          ))}
          <label className="check" title={nmsTip(o.target)}><input type="checkbox" checked={o.include_nms} disabled={fixed} data-testid="onnx-nms" onChange={(e) => set({ include_nms: e.target.checked, ...(e.target.checked ? { dynamic_batch: false } : {}) })} />Include NMS in the model</label>
          <label className="check" title={"On: the export carries the confidence and NMS overlap measured for this model on its validation images (the pair with the best balance of found objects and right boxes), and a device that reads them starts there.\nOff: type your own."}>
            <input type="checkbox" checked={o.auto_thresholds} data-testid="onnx-auto-thresholds" onChange={(e) => set({ auto_thresholds: e.target.checked, ...(e.target.checked || !recommended ? {} : { conf: recommended.conf, iou: recommended.iou }) })} />
            Use the thresholds measured for this model</label>
          <div className="muted" data-testid="onnx-thresholds-line">{thresholdsLine(recommended)}</div>
          {row("Confidence threshold:", num("conf", 0.01, 0.99, 0.05, !o.auto_thresholds, "onnx-conf"))}
          {row("NMS IoU threshold:", num("iou", 0.05, 0.95, 0.05, !o.auto_thresholds, "onnx-iou"))}
          {row("Max detections:", num("max_det", 1, 1000, 1, o.include_nms, "onnx-maxdet"))}
          <label className="check" title={"Leave off for ctrlX. A static shape is what lets the GPU plan memory ahead,\nand TensorRT needs one unless you supply an optimization profile.\nTick it only if you will feed more than one image at a time.\nNot available when NMS is included — that graph is always batch 1."}>
            <input type="checkbox" checked={o.dynamic_batch} disabled={fixed || o.include_nms} data-testid="onnx-dynamic" onChange={(e) => set({ dynamic_batch: e.target.checked })} />Allow variable batch size</label>
          <label className="check" title={fixed ? "Always on for this target: the files that travel with the model are needed to run it." : "Adds classes.txt, metadata.json and setup notes alongside the .onnx."}>
            <input type="checkbox" checked={o.bundle} disabled={fixed} data-testid="onnx-bundle" onChange={(e) => set({ bundle: e.target.checked })} />Save as .zip with class list and README</label>
          {row("Calibration images:", (
            <input className="input" type="number" min={8} max={2000} value={o.calib_limit} disabled={o.target !== "hailo"} data-testid="onnx-calib" style={{ width: 110 }}
              title="How many of this project's images to letterbox into the INT8 calibration set. Hailo recommends at least 64; a few hundred is typical. More is slower to export and to compile."
              onChange={(e) => set({ calib_limit: Math.min(2000, Math.max(8, Number(e.target.value))) })} />
          ))}
          <div className="muted" data-testid="onnx-hint">
            {o.include_nms ? "Outputs: detections (1, D, 6) — x1, y1, x2, y2, score, class_id — plus num_detections."
              : "Outputs: boxes (B, N, 4) and scores (B, N, classes). Your app applies the confidence filter and NMS."}
          </div>
        </div>
      </details>

      <div className="dialog-actions">
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className={`btn ${compiles ? "" : "primary"}`} data-testid="onnx-go" onClick={() => onExport(effectiveOnnx(o))}>Export</button>
        {compiles && <button className="btn primary" data-testid="onnx-compile" onClick={() => onCompile!(effectiveOnnx(o))}>Compile to .hef</button>}
      </div>
    </Dialog>
  );
}
